import { useState } from 'react';
import ClassNames from 'classnames';
import { useFeatureFlag } from 'configcat-react';

interface MediaDeviceInfoLike {
	deviceId: string;
	label: string;
}

type Props = {
	isToaster: boolean;
	studentId?: number | null;
	isWhiteBoard: boolean;
	activeMicrophone: boolean;
	activeVideoCamera: boolean;
	activeScreenShare: boolean;
	activeMicrophoneDevice: string;
	toggleMicrophone: () => void;
	toggleVideoCamera: () => void;
	toggleViewType: () => void;
	switchScreenShare: () => void;
	onToggleToaster: () => void;
	toggleWhiteBoard: () => void;
	onLeave: () => void;
	microphoneDevices?: MediaDeviceInfoLike[];
	cameraDevices?: MediaDeviceInfoLike[];
	onCameraSourceChange?: (deviceId: string) => void;
	onAudioInputSourceChange: (deviceId: string) => void;
	issueReportEnabled: boolean;
	onIssueReportClick?: () => void;
	onToggleMessages?: () => void;
	isInWebView?: boolean;
	hasUnreadMessages?: boolean;
	isActiveMessages?: boolean;
};

export const MeetCameraBottom = ({
	isToaster,
	isWhiteBoard,
	toggleWhiteBoard,
	activeMicrophone,
	activeVideoCamera,
	toggleVideoCamera,
	toggleMicrophone,
	toggleViewType,
	activeScreenShare,
	switchScreenShare,
	onToggleToaster,
	onLeave,
	microphoneDevices = [],
	cameraDevices = [],
	onCameraSourceChange,
	onAudioInputSourceChange,
	activeMicrophoneDevice,
	issueReportEnabled,
	onIssueReportClick,
	isInWebView,
	onToggleMessages,
	isActiveMessages = false,
	hasUnreadMessages = false
}: Props) => {
	const [microphoneDevicesToggle, setMicrophoneDevicesToggle] = useState(false);
	const [cameraDevicesToggle, setCameraDevicesToggle] = useState(false);

	const { value: isActiveIssueReportButton } = useFeatureFlag('isActiveIssueReportButton', false);
	const { value: isActiveMeetMessageButton } = useFeatureFlag('isActiveMeetMessageButton', false);

	const onOpenMicrophoneDevices = () => {
		setMicrophoneDevicesToggle(!microphoneDevicesToggle);
		setCameraDevicesToggle(false);
	};

	const onOpenCameraDevices = () => {
		setCameraDevicesToggle(!cameraDevicesToggle);
		setMicrophoneDevicesToggle(false);
	};

	return (
		<div className={ClassNames('meet-bottom', { toaster: isToaster })}>
			{!isToaster && isActiveIssueReportButton && (
				<div
					className={ClassNames('issue-report', { 'disable-issue-report': !issueReportEnabled })}
					onClick={onIssueReportClick}>
					<div className="circle-red">
						<i className="icons-danger" />
					</div>
					<div className="issue-report-txt">Bağlantı Sorunu Bildir</div>
				</div>
			)}

			<div className={ClassNames('button-group', { toaster: isToaster })}>
				<>
					<div
						className={ClassNames('btn-upper-group', {
							active: microphoneDevices.length > 0,
							toaster: isToaster
						})}>
						{microphoneDevicesToggle && (
							<div
								className={ClassNames('btn-option-groups', {
									toaster: isToaster
								})}>
								{microphoneDevices.map((deviceItem, index) => (
									<div
										key={`microphone-device-${index}`}
										className={ClassNames('btn-option', {
											'active-option': activeMicrophoneDevice === deviceItem?.deviceId
										})}
										onClick={() => onAudioInputSourceChange(deviceItem?.deviceId)}>
										<i className="icons-microphone" />
										{deviceItem.label}
									</div>
								))}
							</div>
						)}

						{microphoneDevices.length > 0 && (
							<div
								className={ClassNames('toggle-up-btn', {
									toggled: microphoneDevicesToggle,
									toaster: isToaster
								})}
								onClick={onOpenMicrophoneDevices}>
								<i className="icons-alt-arrow-up" />
							</div>
						)}

						<div
							className={ClassNames('btn-item', {
								active: activeMicrophone,
								toaster: isToaster
							})}
							onClick={toggleMicrophone}>
							<i className="icons-microphone" />
							<span className="tooltip-text">{activeMicrophone ? 'Mikrofonu Kapat' : 'Mikrofonu Aç'}</span>
						</div>
					</div>

					<div
						className={ClassNames('btn-upper-group', {
							active: cameraDevices.length > 1,
							toaster: isToaster
						})}>
						{cameraDevicesToggle && cameraDevices?.length > 1 && (
							<div
								className={ClassNames('btn-option-groups', {
									toaster: isToaster
								})}>
								{cameraDevices.map((deviceItem, index) => (
									<div
										key={`camera-device-${index}`}
										className="btn-option"
										onClick={() => onCameraSourceChange?.(deviceItem.deviceId)}>
										<i className="icons-videocamera" />
										{deviceItem.label}
									</div>
								))}
							</div>
						)}

						{cameraDevices.length > 1 && (
							<div
								className={ClassNames('toggle-up-btn', {
									toggled: cameraDevicesToggle,
									toaster: isToaster
								})}
								onClick={onOpenCameraDevices}>
								<i className="icons-alt-arrow-up" />
							</div>
						)}

						<div
							className={ClassNames('btn-item', {
								active: activeVideoCamera,
								toaster: isToaster
							})}
							onClick={toggleVideoCamera}>
							<i className="icons-videocamera" />
							<span className="tooltip-text">{activeVideoCamera ? 'Kamerayı Kapat' : 'Kamerayı Aç'}</span>
						</div>
					</div>
				</>

				<div
					className={ClassNames('btn-item', {
						active: isWhiteBoard,
						toaster: isToaster
					})}
					onClick={toggleWhiteBoard}>
					<i className="icons-whiteboard" />
					<span className="tooltip-text">{isWhiteBoard ? 'Beyaz Tahtayı Kapat' : 'Beyaz Tahtayı Aç'}</span>
				</div>
				{!isToaster && !isInWebView && (
					<>
						<div
							className={ClassNames('btn-item', 'hide-mobile', {
								active: activeScreenShare,
								toaster: isToaster
							})}
							onClick={switchScreenShare}>
							<i className="icons-send-square" />
							<span className="tooltip-text">{activeScreenShare ? 'Ekran Paylaşımını Kapat' : 'Ekran Paylaşımını Başlat'}</span>
						</div>
						<div
							className={ClassNames('btn-item', 'hide-mobile', {
								toaster: isToaster
							})}
							onClick={toggleViewType}>
							<i className="icons-widget" />
							<span className="tooltip-text">Görünümü Değiştir</span>
						</div>
					</>
				)}

				<div
					className={ClassNames('btn-item', 'hide-mobile', {
						active: isToaster,
						toaster: isToaster
					})}
					onClick={onToggleToaster}>
					<i className="icons-minimize-square-3" />
					<span className="tooltip-text">{isToaster ? 'Küçük Ekran Modundan Çık' : 'Küçük Ekran Moduna Geç'}</span>
				</div>

				{!isToaster && isActiveMeetMessageButton && (
					<div
						className={ClassNames('btn-item', {
							active: isActiveMessages,
							toaster: isToaster
						})}
						onClick={onToggleMessages}>
						<i className="icons-conversation" />
						<span className="tooltip-text">Mesajlar</span>
						{hasUnreadMessages && !isActiveMessages && <div className="has-unread-message" />}
					</div>
				)}

				{isToaster && (
					<div
						className={ClassNames('btn-item call-btn', {
							active: isToaster,
							toaster: isToaster
						})}
						onClick={onLeave}>
						<i className="icons-end-call" />
						<span className="tooltip-text">Aramayı Sonlandır</span>
					</div>
				)}
			</div>
		</div>
	);
};
